import React, { useCallback, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import AWS from 'aws-sdk';
import { FiUpload, FiCheck, FiX } from 'react-icons/fi';
import ProcessingSteps from './ProcessingSteps';
import config from '../config';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const UploadContainer = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;

  &:hover, &.dragging {
    border-color: #3b82f6;
    background: #f8fafc;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .upload-icon-wrapper {
    background: #eff6ff;
    padding: 0.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .upload-icon {
    color: #3b82f6;
    width: 24px;
    height: 24px;
  }

  .upload-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .upload-title {
    color: #1e293b;
    font-size: 1rem;
    font-weight: 500;
  }

  .upload-text {
    color: #64748b;
    font-size: 0.875rem;
  }

  &:hover .upload-icon-wrapper {
    background: #3b82f6;
    transform: scale(1.05);
  }

  &:hover .upload-icon {
    color: #ffffff;
  }

  &.dragging {
    border-width: 3px;
    .upload-icon-wrapper {
      background: #3b82f6;
      transform: scale(1.1);
    }
    .upload-icon {
      color: #ffffff;
    }
    .upload-title {
      color: #3b82f6;
    }
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: #0088cc;
  margin-bottom: 20px;
`;

const UploadText = styled.div`
  color: #666;
  font-size: 16px;
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Notification = styled.div`
  padding: 15px 25px;
  border-radius: 4px;
  background: ${props => props.type === 'success' ? '#27ae60' : '#e74c3c'};
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  pointer-events: auto;
  animation: ${props => props.isLeaving ? 'slideOut' : 'slideIn'} 0.5s ease forwards;
  position: relative;
  
  @keyframes slideIn {
    from {
      transform: translateX(120%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(120%);
      opacity: 0;
    }
  }
`;

const NotificationMessage = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const UploadArea = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showProcessing, setShowProcessing] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const notificationIdCounter = useRef(0);

  const showNotification = useCallback((message, type) => {
    const id = notificationIdCounter.current++;
    
    setNotifications(prev => [...prev, { id, message, type, isLeaving: false }]);

    // Start the auto-dismiss timer
    setTimeout(() => {
      dismissNotification(id);
    }, 5000);

    return id;
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id 
          ? { ...notif, isLeaving: true }
          : notif
      )
    );

    // Remove notification after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 500);
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      try {
        console.log('Starting upload for file:', file.name);
        
        // Get presigned URL from backend
        const presignedResponse = await fetch(`${config.API_BASE_URL}/get-presigned-url?filename=${encodeURIComponent(file.name)}&type=${encodeURIComponent(file.type)}`);
        const presignedData = await presignedResponse.json();
        
        if (presignedData.error) {
          throw new Error(presignedData.error);
        }

        console.log('Got presigned URL, starting upload');

        // Upload file using presigned URL
        const uploadResponse = await fetch(presignedData.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'private'
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status: ${uploadResponse.status}`);
        }

        console.log('S3 upload successful');

        // Show success notification
        showNotification(`${file.name} uploaded successfully!`, 'success');

        // Set current document and show processing steps
        setCurrentDocument({
          name: file.name,
          id: Date.now().toString() // Using timestamp as temporary ID
        });
        setShowProcessing(true);

        // Wait for processing steps to complete
        setTimeout(async () => {
          if (onUploadComplete) {
            try {
              await onUploadComplete();
            } catch (error) {
              console.error('Error refreshing data:', error);
              showNotification('Failed to refresh data. Please reload the page.', 'error');
            }
          }
        }, 270000); // Wait for processing steps to complete (260s = ~4.5 min) + buffer (10s)

      } catch (error) {
        console.error('Upload error details:', {
          name: error.name,
          message: error.message,
          code: error.code,
          statusCode: error.statusCode,
          requestId: error.requestId
        });

        let errorMessage;
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.code === 'NetworkingError') {
          errorMessage = 'Network error occurred. Please check your connection.';
        } else if (error.code === 'NoSuchBucket') {
          errorMessage = 'S3 bucket not found. Please check configuration.';
        } else if (error.code === 'AccessDenied') {
          errorMessage = 'Access denied to S3 bucket. Please check permissions.';
        } else if (error.code === 'InvalidAccessKeyId') {
          errorMessage = 'Invalid AWS credentials.';
        } else if (error.code === 'RequestTimeout') {
          errorMessage = 'Upload timed out. Please try again.';
        } else if (error.message) {
          errorMessage = error.message;
        } else {
          errorMessage = 'Failed to upload file. Please try again.';
        }
        
        showNotification(errorMessage, 'error');
      }
    }
    
    setUploading(false);
  }, [onUploadComplete, showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    disabled: uploading,
    maxSize: 50 * 1024 * 1024, // 50MB max file size
    preventDropOnDocument: true,
    noClick: uploading
  });

  const rootProps = getRootProps();

  return (
    <Container>
      <UploadContainer 
        {...rootProps}
        onClick={(e) => {
          e.preventDefault();
          if (!uploading) {
            rootProps.onClick(e);
          }
        }}
        style={{ opacity: uploading ? 0.6 : 1 }}
        className={isDragActive ? 'dragging' : ''}
      >
        <input {...getInputProps()} />
        <div className="upload-icon-wrapper">
          <FiUpload className="upload-icon" />
        </div>
        <div className="upload-content">
          <div className="upload-title">
            {uploading ? 'Uploading documents...' :
             isDragActive ? 'Drop your documents here' :
             'Upload Documents'}
          </div>
          <div className="upload-text">
            {uploading ? 'Please wait while we process your files' :
             isDragActive ? 'Release to start uploading' :
             'Drag and drop your PDF files here, or click to browse'}
          </div>
        </div>
      </UploadContainer>
      
      <NotificationContainer>
        {notifications.map(({ id, message, type, isLeaving }) => (
          <Notification key={id} type={type} isLeaving={isLeaving}>
            {type === 'success' ? (
              <FiCheck size={20} />
            ) : (
              <FiX size={20} />
            )}
            <NotificationMessage>{message}</NotificationMessage>
            <CloseButton onClick={() => dismissNotification(id)}>
              <FiX size={16} />
            </CloseButton>
          </Notification>
        ))}
      </NotificationContainer>

      {currentDocument && (
        <ProcessingSteps
          isOpen={showProcessing}
          documentName={currentDocument.name}
          documentId={currentDocument.id}
          onClose={() => {
            setShowProcessing(false);
            setCurrentDocument(null);
          }}
        />
      )}
    </Container>
  );
};

export default UploadArea;